import { useState, useEffect } from "react";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Wrench, Package, RotateCcw, Download, FileText, FileSpreadsheet, ChevronRight, ChevronDown } from "lucide-react";
import { exportCSV, exportExcel, exportPDF } from "../components/TableExport";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";

const CATEGORIES = [
  { value: "all", label: "Toutes les categories" },
  { value: "mecanique", label: "Mecanique" },
  { value: "carrosserie", label: "Carrosserie" },
  { value: "electrique", label: "Electrique" },
  { value: "pneumatique", label: "Pneumatique" },
  { value: "frais_gestion", label: "Frais de gestion" },
  { value: "autre", label: "Autre" },
];

export default function InvestorRepairsPage() {
  const [repairs, setRepairs] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateDebut, setDateDebut] = useState("");
  const [dateFin, setDateFin] = useState("");
  const [vehiculeId, setVehiculeId] = useState("all");
  const [categorie, setCategorie] = useState("all");
  const [expandedRows, setExpandedRows] = useState({});
  const toggleRow = (id) => setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));

  const fetchData = async () => {
    setLoading(true);
    try {
      let url = "/investor/reparations?";
      if (dateDebut) url += `date_debut=${dateDebut}&`;
      if (dateFin) url += `date_fin=${dateFin}&`;
      if (vehiculeId && vehiculeId !== "all") url += `vehicule_id=${vehiculeId}&`;
      if (categorie && categorie !== "all") url += `categorie=${categorie}&`;
      const [rRes, vRes] = await Promise.all([api.get(url), api.get("/vehicles")]);
      setRepairs(rRes.data);
      setVehicles(vRes.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [dateDebut, dateFin, vehiculeId, categorie]);

  const resetFilters = () => {
    setDateDebut("");
    setDateFin("");
    setVehiculeId("all");
    setCategorie("all");
  };

  const totalCout = repairs.reduce((s, r) => s + (r.total || 0), 0);
  const totalPieces = repairs.reduce((s, r) => s + (r.pieces || []).length, 0);

  const repairsExportCols = [
    { label: "Date", accessor: (r) => r.date_reparation || "-" },
    { label: "Vehicule", accessor: (r) => `${r.vehicule_nom} (${r.vehicule_immat})` },
    { label: "Description", key: "description" },
    { label: "Categorie", accessor: (r) => r.categorie || "autre" },
    { label: "Pieces", accessor: (r) => (r.pieces || []).map(p => p.nom).join(", ") || "-" },
    { label: "Cout total", key: "total" },
    { label: "Statut", accessor: (r) => r.statut === "termine" ? "Termine" : "En cours" },
  ];

  return (
    <div data-testid="investor-repairs-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Mes Reparations</h1>
          <p className="text-sm text-slate-500 mt-1">{repairs.length} reparation{repairs.length > 1 ? "s" : ""}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <Select value={vehiculeId} onValueChange={setVehiculeId}>
            <SelectTrigger className="w-full sm:w-[200px]" data-testid="filter-vehicle">
              <SelectValue placeholder="Tous les vehicules" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les vehicules</SelectItem>
              {vehicles.map(v => (
                <SelectItem key={v.id} value={v.id}>{v.marque} {v.modele} - {v.immatriculation}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={categorie} onValueChange={setCategorie}>
            <SelectTrigger className="w-full sm:w-[200px]" data-testid="filter-category">
              <SelectValue placeholder="Categorie" />
            </SelectTrigger>
            <SelectContent>
              {CATEGORIES.map(c => (
                <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-1">
            <Label className="text-xs text-slate-500 whitespace-nowrap">Du</Label>
            <Input type="date" value={dateDebut} onChange={e => setDateDebut(e.target.value)} className="w-[140px] text-sm" data-testid="filter-date-start" />
          </div>
          <div className="flex items-center gap-1">
            <Label className="text-xs text-slate-500 whitespace-nowrap">Au</Label>
            <Input type="date" value={dateFin} onChange={e => setDateFin(e.target.value)} className="w-[140px] text-sm" data-testid="filter-date-end" />
          </div>
          <Button variant="outline" size="sm" onClick={resetFilters} data-testid="reset-filters-btn">
            <RotateCcw className="w-4 h-4 mr-1" />Reinitialiser
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" data-testid="btn-export-inv-repairs">
                <Download className="w-4 h-4 mr-2" />Exporter
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => exportCSV(repairs, repairsExportCols, "reparations-investisseur")} data-testid="export-inv-repairs-csv"><FileText className="w-4 h-4 mr-2" />CSV</DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportExcel(repairs, repairsExportCols, "reparations-investisseur")} data-testid="export-inv-repairs-excel"><FileSpreadsheet className="w-4 h-4 mr-2" />Excel</DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportPDF(repairs, repairsExportCols, "reparations-investisseur", "Mes Reparations")} data-testid="export-inv-repairs-pdf"><FileText className="w-4 h-4 mr-2" />PDF</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8" data-testid="repairs-summary">
        <div className="stat-card">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center"><Wrench className="w-5 h-5 text-red-600" /></div>
          </div>
          <p className="text-2xl font-bold text-slate-900 font-heading">{totalCout} Frc</p>
          <p className="text-xs text-slate-500 mt-1">Cout total</p>
        </div>
        <div className="stat-card">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center"><Wrench className="w-5 h-5 text-[#0F52BA]" /></div>
          </div>
          <p className="text-2xl font-bold text-slate-900 font-heading">{repairs.length}</p>
          <p className="text-xs text-slate-500 mt-1">Nombre de reparations</p>
        </div>
        <div className="stat-card">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center"><Package className="w-5 h-5 text-amber-600" /></div>
          </div>
          <p className="text-2xl font-bold text-slate-900 font-heading">{totalPieces}</p>
          <p className="text-xs text-slate-500 mt-1">Pieces utilisees</p>
        </div>
      </div>

      {/* Repairs Table */}
      {loading ? (
        <div className="text-slate-500 text-center py-12">Chargement...</div>
      ) : (
        <div className="table-container">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50/50">
                <TableHead className="font-semibold text-slate-600">Date</TableHead>
                <TableHead className="font-semibold text-slate-600">Vehicule</TableHead>
                <TableHead className="font-semibold text-slate-600">Description</TableHead>
                <TableHead className="font-semibold text-slate-600">Categorie</TableHead>
                <TableHead className="font-semibold text-slate-600">Pieces</TableHead>
                <TableHead className="font-semibold text-slate-600">Cout total</TableHead>
                <TableHead className="font-semibold text-slate-600">Statut</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {repairs.map((r, i) => {
                const hasPieces = (r.pieces || []).length > 0;
                const hasDetails = hasPieces || r.main_oeuvre > 0 || r.frais_annexes > 0;
                const isExpanded = expandedRows[r.id || i];
                return (
                  <>
                    <TableRow key={r.id || i} data-testid={`repair-row-${i}`} className={hasDetails ? "cursor-pointer hover:bg-slate-50/80" : ""} onClick={() => hasDetails && toggleRow(r.id || i)}>
                      <TableCell className="text-slate-600">
                        <div className="flex items-center gap-1.5">
                          {hasDetails && (isExpanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />)}
                          {r.date_reparation || "-"}
                        </div>
                      </TableCell>
                      <TableCell className="font-medium text-slate-900">
                        {r.vehicule_nom}<br />
                        <span className="text-xs text-slate-500">{r.vehicule_immat}</span>
                      </TableCell>
                      <TableCell className="text-slate-700 max-w-[200px] truncate">{r.description}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">{r.categorie || "autre"}</Badge>
                      </TableCell>
                      <TableCell className="text-slate-600">
                        {hasPieces ? `${(r.pieces || []).length} piece(s)` : "-"}
                      </TableCell>
                      <TableCell className="text-red-600 font-medium">{r.total} Frc</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={
                          r.statut === "termine" ? "border-emerald-200 text-emerald-700 bg-emerald-50" :
                          "border-amber-200 text-amber-700 bg-amber-50"
                        }>{r.statut === "termine" ? "Termine" : "En cours"}</Badge>
                      </TableCell>
                    </TableRow>
                    {hasDetails && isExpanded && (
                      <TableRow key={`${r.id || i}-detail`} data-testid={`repair-detail-${i}`}>
                        <TableCell colSpan={7} className="bg-slate-50/70 py-3 px-6">
                          {hasPieces && (
                            <>
                              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Detail des pieces</div>
                              <div className="space-y-1.5 mb-3">
                                {(r.pieces || []).map((p, idx) => (
                                  <div key={idx} className="flex items-center justify-between text-sm bg-white rounded px-3 py-1.5 border border-slate-100">
                                    <span className="text-slate-700 font-medium">{p.nom}</span>
                                    <span className="text-slate-900 font-semibold">{p.prix || 0} Frc</span>
                                  </div>
                                ))}
                              </div>
                            </>
                          )}
                          <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                            {hasPieces && <span>Sous-total pieces : <strong>{(r.pieces || []).reduce((s, p) => s + (p.prix || 0), 0)} Frc</strong></span>}
                            {r.main_oeuvre > 0 && <span>Main d'oeuvre : <strong>{r.main_oeuvre} Frc</strong></span>}
                            {r.frais_annexes > 0 && <span>Frais annexes : <strong>{r.frais_annexes} Frc</strong></span>}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                );
              })}
              {repairs.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-8">Aucune reparation trouvee</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
